import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { db } from '../firebase';
import { collection, doc, setDoc, getDocs, deleteDoc } from 'firebase/firestore';
import { useAuth } from './AuthContext';

const LOCAL_STORAGE_KEY = 'ai_ebook_projects'; // kept as offline fallback

// Initial state
const initialState = {
  // Step tracking
  currentStep: 1,
  totalSteps: 6,

  // Step 1: Topic Discovery
  originalTopic: '',
  titles: [], // 5-7 magnetic titles

  // Step 2: Title Selection
  selectedTitle: '',

  // Step 3: Outline Generation (TOC)
  chapters: [], // 12 chapters with 4-6 sub-topics each

  // Step 4: Content Authoring
  currentChapterIndex: 0,
  contentGenerated: false,

  // Step 5: Cover Design
  coverData: {
    title: '',
    subtitle: '',
    authorName: '',
    stylePrompt: '',
    generatedImages: [],
    selectedImage: null
  },

  // Step 6: KDP Formatting & Export
  kdpSettings: {
    trimSize: '6x9', // Options: 5x8, 5.25x8, 5.5x8.5, 6x9, 7x10
    paperType: 'white', // white, cream, color
    fontFamily: 'Garamond', // Garamond, Palatino, Times New Roman
    fontSize: 11, // 10, 11, 12
    bleed: true
  },

  // Front Matter
  frontMatter: {
    dedication: '',
    preface: '',
    introduction: ''
  },

  // Back Matter
  backMatter: {
    aboutAuthor: '',
    resources: ''
  },

  // KDP Metadata
  kdpMetadata: {
    description: '',
    keywords: [],
    categories: [],
    targetAudience: '',
    language: 'English',
    copyrightYear: new Date().getFullYear().toString()
  },

  // KDP Compliance
  complianceCheck: null,

  // UI state
  loading: false,
  error: '',
};

// Action types
const ActionTypes = {
  SET_STEP: 'SET_STEP',
  SET_ORIGINAL_TOPIC: 'SET_ORIGINAL_TOPIC',
  SET_TITLES: 'SET_TITLES',
  SET_SELECTED_TITLE: 'SET_SELECTED_TITLE',
  SET_CHAPTERS: 'SET_CHAPTERS',
  SET_CHAPTER_CONTENT: 'SET_CHAPTER_CONTENT',
  SET_CURRENT_CHAPTER: 'SET_CURRENT_CHAPTER',
  SET_COVER_DATA: 'SET_COVER_DATA',
  SET_KDP_SETTINGS: 'SET_KDP_SETTINGS',
  SET_FRONT_MATTER: 'SET_FRONT_MATTER',
  SET_BACK_MATTER: 'SET_BACK_MATTER',
  SET_KDP_METADATA: 'SET_KDP_METADATA',
  SET_COMPLIANCE_CHECK: 'SET_COMPLIANCE_CHECK',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  RESET_PROJECT: 'RESET_PROJECT',
  LOAD_PROJECT: 'LOAD_PROJECT',
};

// Reducer
function bookReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_STEP:
      return { ...state, currentStep: action.payload };

    case ActionTypes.SET_ORIGINAL_TOPIC:
      return { ...state, originalTopic: action.payload };

    case ActionTypes.SET_TITLES:
      return { ...state, titles: action.payload };

    case ActionTypes.SET_SELECTED_TITLE:
      return { ...state, selectedTitle: action.payload };

    case ActionTypes.SET_CHAPTERS:
      return { ...state, chapters: action.payload };

    case ActionTypes.SET_CHAPTER_CONTENT: {
      const { chapterIndex, content } = action.payload;
      const updatedChapters = [...state.chapters];
      updatedChapters[chapterIndex] = {
        ...updatedChapters[chapterIndex],
        content,
        edited: true
      };
      return { ...state, chapters: updatedChapters };
    }

    case ActionTypes.SET_CURRENT_CHAPTER:
      return { ...state, currentChapterIndex: action.payload };

    case ActionTypes.SET_COVER_DATA:
      return { ...state, coverData: { ...state.coverData, ...action.payload } };

    case ActionTypes.SET_KDP_SETTINGS:
      return { ...state, kdpSettings: { ...state.kdpSettings, ...action.payload } };

    case ActionTypes.SET_FRONT_MATTER:
      return { ...state, frontMatter: { ...state.frontMatter, ...action.payload } };

    case ActionTypes.SET_BACK_MATTER:
      return { ...state, backMatter: { ...state.backMatter, ...action.payload } };

    case ActionTypes.SET_KDP_METADATA:
      return { ...state, kdpMetadata: { ...state.kdpMetadata, ...action.payload } };

    case ActionTypes.SET_COMPLIANCE_CHECK:
      return { ...state, complianceCheck: action.payload };

    case ActionTypes.SET_LOADING:
      return { ...state, loading: action.payload };

    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload };

    case ActionTypes.RESET_PROJECT:
      return { ...initialState, currentStep: 1 };

    case ActionTypes.LOAD_PROJECT:
      return { ...initialState, ...action.payload };

    default:
      return state;
  }
}

// Create Context
const BookContext = createContext();

// Provider Component
export function BookProvider({ children }) {
  const [state, dispatch] = useReducer(bookReducer, initialState);
  const { user } = useAuth();
  const userId = user?.uid || null;

  // Auto-save to Firestore on state changes
  useEffect(() => {
    if (!userId || !state.selectedTitle) return;

    const saveToFirestore = async () => {
      try {
        const projectRef = doc(db, 'users', userId, 'projects', state.selectedTitle);
        await setDoc(projectRef, { ...state, savedAt: new Date().toISOString() }, { merge: true });
      } catch (e) {
        // Fallback to localStorage silently
        const projects = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '{}');
        projects[state.selectedTitle] = { ...state, savedAt: new Date().toISOString() };
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(projects));
      }
    };

    const debounce = setTimeout(saveToFirestore, 1500);
    return () => clearTimeout(debounce);
  }, [state.chapters, state.selectedTitle, state.coverData, state.kdpSettings, state.frontMatter, state.backMatter, state.kdpMetadata, userId]);

  // Actions
  const setStep = useCallback((step) => {
    dispatch({ type: ActionTypes.SET_STEP, payload: step });
  }, []);

  const setOriginalTopic = useCallback((topic) => {
    dispatch({ type: ActionTypes.SET_ORIGINAL_TOPIC, payload: topic });
  }, []);

  const setTitles = useCallback((titles) => {
    dispatch({ type: ActionTypes.SET_TITLES, payload: titles });
  }, []);

  const setSelectedTitle = useCallback((title) => {
    dispatch({ type: ActionTypes.SET_SELECTED_TITLE, payload: title });
  }, []);

  const setChapters = useCallback((chapters) => {
    dispatch({ type: ActionTypes.SET_CHAPTERS, payload: chapters });
  }, []);

  const setChapterContent = useCallback((chapterIndex, content) => {
    dispatch({ type: ActionTypes.SET_CHAPTER_CONTENT, payload: { chapterIndex, content } });
  }, []);

  const setCurrentChapter = useCallback((index) => {
    dispatch({ type: ActionTypes.SET_CURRENT_CHAPTER, payload: index });
  }, []);

  const setCoverData = useCallback((data) => {
    dispatch({ type: ActionTypes.SET_COVER_DATA, payload: data });
  }, []);

  const setKdpSettings = useCallback((settings) => {
    dispatch({ type: ActionTypes.SET_KDP_SETTINGS, payload: settings });
  }, []);

  const setFrontMatter = useCallback((data) => {
    dispatch({ type: ActionTypes.SET_FRONT_MATTER, payload: data });
  }, []);

  const setBackMatter = useCallback((data) => {
    dispatch({ type: ActionTypes.SET_BACK_MATTER, payload: data });
  }, []);

  const setKdpMetadata = useCallback((data) => {
    dispatch({ type: ActionTypes.SET_KDP_METADATA, payload: data });
  }, []);

  const setComplianceCheck = useCallback((data) => {
    dispatch({ type: ActionTypes.SET_COMPLIANCE_CHECK, payload: data });
  }, []);

  const setLoading = useCallback((loading) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: loading });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: ActionTypes.SET_ERROR, payload: error });
  }, []);

  const resetProject = useCallback(() => {
    dispatch({ type: ActionTypes.RESET_PROJECT });
  }, []);

  const saveProject = useCallback(async (name) => {
    const saveName = name || state.selectedTitle || `Draft_${Date.now()}`;
    if (!userId) return saveName;
    try {
      const projectRef = doc(db, 'users', userId, 'projects', saveName);
      await setDoc(projectRef, { ...state, savedAt: new Date().toISOString() });
    } catch (e) {
      const projects = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '{}');
      projects[saveName] = { ...state, savedAt: new Date().toISOString() };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(projects));
    }
    return saveName;
  }, [state, userId]);

  const loadProject = useCallback((projectData) => {
    dispatch({ type: ActionTypes.LOAD_PROJECT, payload: projectData });
    return true;
  }, []);

  const listSavedProjects = useCallback(async () => {
    const uid = userId;
    if (!uid) return [];
    try {
      const colRef = collection(db, 'users', uid, 'projects');
      const snapshot = await getDocs(colRef);
      return snapshot.docs.map(d => {
        const data = d.data();
        return {
          name: d.id,
          savedAt: data.savedAt,
          step: data.currentStep,
          chaptersGenerated: (data.chapters || []).filter(c => c.content).length,
          data,
        };
      }).sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));
    } catch (e) {
      return [];
    }
  }, [userId]);

  const deleteProject = useCallback(async (name) => {
    const uid = userId;
    if (!uid) return;
    try {
      await deleteDoc(doc(db, 'users', uid, 'projects', name));
    } catch (e) {
      const projects = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '{}');
      delete projects[name];
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(projects));
    }
  }, [userId]);

  // Helper: Calculate KDP margins based on page count
  const calculateKdpMargins = useCallback((pageCount) => {
    const { trimSize } = state.kdpSettings;
    const [, height] = trimSize.split('x').map(s => parseFloat(s));

    let top = 0.75;
    let bottom = 0.75;
    let outside = 0.625;
    let gutter = 0.5;

    if (pageCount <= 150) {
      gutter = 0.375;
    } else if (pageCount <= 300) {
      gutter = 0.5;
    } else if (pageCount <= 500) {
      gutter = 0.625;
    } else if (pageCount <= 700) {
      gutter = 0.75;
    } else {
      gutter = 0.875;
    }

    if (height >= 10) {
      top = bottom = 1.0;
    } else if (height >= 9) {
      top = bottom = 0.85;
    }

    return { top, bottom, outside, gutter };
  }, [state.kdpSettings]);

  // Helper: Estimate page count (includes front/back matter)
  const estimatePageCount = useCallback(() => {
    const chapterWords = state.chapters.reduce((sum, ch) => {
      return sum + (ch.content ? ch.content.split(/\s+/).length : 0);
    }, 0);

    const frontMatterWords =
      (state.frontMatter.introduction ? state.frontMatter.introduction.split(/\s+/).length : 0) +
      (state.frontMatter.preface ? state.frontMatter.preface.split(/\s+/).length : 0) +
      (state.frontMatter.dedication ? 50 : 0); // dedication is usually short

    const backMatterWords =
      (state.backMatter.aboutAuthor ? state.backMatter.aboutAuthor.split(/\s+/).length : 0) +
      (state.backMatter.resources ? state.backMatter.resources.split(/\s+/).length : 0);

    const totalWords = chapterWords + frontMatterWords + backMatterWords;

    // Add extra pages for title page, copyright, TOC, blank pages
    const contentPages = Math.ceil(totalWords / 250);
    const structuralPages = 6; // title, copyright, dedication, TOC, blank pages
    return Math.max(contentPages + structuralPages, 24); // KDP minimum 24 pages
  }, [state.chapters, state.frontMatter, state.backMatter]);

  // Helper: Calculate spine width
  const calculateSpineWidth = useCallback((pageCount) => {
    const { paperType } = state.kdpSettings;
    const coefficients = {
      white: 0.002252,
      cream: 0.0025,
      color: 0.002347
    };
    const coefficient = coefficients[paperType] || coefficients.white;
    return pageCount * coefficient;
  }, [state.kdpSettings]);

  // Helper: Get total word count
  const getTotalWordCount = useCallback(() => {
    return state.chapters.reduce((sum, ch) => {
      return sum + (ch.content ? ch.content.split(/\s+/).length : 0);
    }, 0);
  }, [state.chapters]);

  const value = {
    state,
    userId,
    actions: {
      setStep,
      setOriginalTopic,
      setTitles,
      setSelectedTitle,
      setChapters,
      setChapterContent,
      setCurrentChapter,
      setCoverData,
      setKdpSettings,
      setFrontMatter,
      setBackMatter,
      setKdpMetadata,
      setComplianceCheck,
      setLoading,
      setError,
      resetProject,
      saveProject,
      loadProject,
      listSavedProjects,
      deleteProject,
    },
    helpers: {
      calculateKdpMargins,
      estimatePageCount,
      calculateSpineWidth,
      getTotalWordCount,
    },
  };

  return <BookContext.Provider value={value}>{children}</BookContext.Provider>;
}

// Custom Hook
export function useBook() {
  const context = useContext(BookContext);
  if (!context) {
    throw new Error('useBook must be used within a BookProvider');
  }
  return context;
}
